import type { BootLoaderArt, BootLoaderPort } from "./BootLoaderPort";

/** The ids the injected markup and this controller agree on; a game that overrides the markup keeps them. */
const IDS = {
  root: "boot-loader",
  background: "boot-loader-bg",
  backgroundPortrait: "boot-loader-bg-portrait",
  logo: "boot-loader-logo",
  fill: "boot-loader-fill",
  status: "boot-loader-status",
  debug: "boot-loader-debug",
} as const;

/** How long the element's fade-out runs before it is removed; matches the CSS transition. */
const DISMISS_FADE_MS = 200;

/**
 * Drives the boot loader that `index.html` already painted.
 *
 * Nothing here creates the loader — the point of the markup living in the document is that the
 * player sees it while the bundle is still downloading, so by the time this runs it is on screen
 * and only needs driving. Easing, layout and the fades are CSS; there is no ticker to subscribe to
 * and no viewport to read, which is also why this survives a renderer that fails to start.
 */
export class DomBootLoader implements BootLoaderPort {
  private readonly root: HTMLElement;
  private readonly background: HTMLImageElement | null;
  /** The background's `<source>` for a portrait viewport (absent in markup that predates it: the one image shows everywhere). */
  private readonly backgroundPortrait: HTMLSourceElement | null;
  private readonly logo: HTMLImageElement | null;
  private readonly fill: HTMLElement | null;
  private readonly status: HTMLElement | null;
  private readonly debug: HTMLElement | null;
  private dismissed = false;

  private constructor(root: HTMLElement) {
    this.root = root;
    const find = <T extends HTMLElement>(id: string): T | null => root.querySelector<T>(`#${id}`);
    this.background = find<HTMLImageElement>(IDS.background);
    this.backgroundPortrait = find<HTMLSourceElement>(IDS.backgroundPortrait);
    this.logo = find<HTMLImageElement>(IDS.logo);
    this.fill = find(IDS.fill);
    this.status = find(IDS.status);
    this.debug = find(IDS.debug);
  }

  /**
   * Adopts the markup if it is there. `null` when it is not — a game that stripped it from its
   * index.html, or `?skipLoader=1`. Boot runs the same either way; it just reports to nothing.
   */
  public static adopt(documentRef: Document = document): DomBootLoader | null {
    const root = documentRef.getElementById(IDS.root);
    return root ? new DomBootLoader(root) : null;
  }

  public setProgress01(value: number): void {
    if (!this.fill) return;
    this.fill.style.width = `${Math.max(0, Math.min(1, value)) * 100}%`;
  }

  public setStatusText(text: string): void {
    if (this.status) this.status.textContent = text;
  }

  public setDebugText(text: string | null): void {
    if (this.debug) this.debug.textContent = text ?? "";
  }

  public setArt(input: BootLoaderArt): void {
    // Setting `src` is what reveals each image: the CSS keeps them transparent until then, so a
    // missing or late asset shows nothing rather than a broken-image box. The portrait source goes
    // first, so a portrait viewport never fetches the other one for nothing.
    if (input.backgroundPortraitUrl !== undefined && this.backgroundPortrait) {
      if (input.backgroundPortraitUrl) this.backgroundPortrait.srcset = input.backgroundPortraitUrl;
      else this.backgroundPortrait.removeAttribute("srcset");
    }
    if (input.backgroundUrl && this.background) this.background.src = input.backgroundUrl;
    if (input.logoUrl && this.logo) this.logo.src = input.logoUrl;
  }

  public dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    this.root.setAttribute("data-dismissed", "");
    // Removed after the fade rather than on a ticker: by this point the game owns the frame budget,
    // and a stray element over a live game is worse than a slightly late removal.
    setTimeout(() => this.root.remove(), DISMISS_FADE_MS);
  }
}
